import { css, html, LitElement } from 'lit'
import { property, query } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-modal')
export class MdcModal extends LitElement {
  @property({ type: String }) content = ''
  @property({ type: Boolean }) open = false
  @property({ type: Boolean, state: true }) private hasCtaSlot = false

  @query('dialog') private dialogEl!: HTMLDialogElement

  private isClosing = false

  private handleClose() {
    this.open = false
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))
  }

  private handleCancel(e: Event) {
    e.preventDefault()
    this.handleClose()
  }

  private openDialog() {
    const dialog = this.dialogEl
    if (!dialog) return
    if (this.isClosing) {
      this.isClosing = false
      dialog.classList.remove('mdc-modal--closing')
      dialog.classList.add('mdc-modal--open')
      return
    }
    if (dialog.open) return
    dialog.showModal()
    requestAnimationFrame(() => dialog.classList.add('mdc-modal--open'))
  }

  private closeDialog() {
    const dialog = this.dialogEl
    if (!dialog || !dialog.open || this.isClosing) return
    this.isClosing = true
    dialog.classList.remove('mdc-modal--open')
    dialog.classList.add('mdc-modal--closing')

    const onEnd = () => {
      this.isClosing = false
      dialog.classList.remove('mdc-modal--closing')
      if (dialog.open) dialog.close()
    }

    const content = dialog.querySelector('.mdc-modal__content') as HTMLElement
    if (content) {
      const handler = (e: TransitionEvent) => {
        if (e.propertyName === 'opacity') {
          content.removeEventListener('transitionend', handler)
          onEnd()
        }
      }
      content.addEventListener('transitionend', handler)
    }

    // Safety timeout if transitionend never fires
    setTimeout(() => {
      if (dialog.open) onEnd()
    }, 350)
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('open')) {
      this.open ? this.openDialog() : this.closeDialog()
    }
  }

  private handleCtaSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement
    const assignedNodes = slot.assignedNodes({ flatten: true })
    this.hasCtaSlot = assignedNodes.some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
    )
  }

  static styles = css`
		:host {
			display: block;
			/* CSS Custom Properties for customization */
			--mdc-modal-overlay-background: var(--color-background-overlay, rgba(17, 17, 17, 0.5));
			--mdc-modal-overlay-transition: opacity 0.3s ease-out;
			--mdc-modal-content-background: var(--color-background-primary, var(--color-functional-gray-white));
			--mdc-modal-content-transition: transform 0.3s ease-out;
			--mdc-modal-content-transition-desktop: opacity 0.3s ease-out, transform 0.3s ease-out;
			--mdc-modal-content-radius-mobile: var(--radius-md, 0.5rem) var(--radius-md, 0.5rem) 0 0;
			--mdc-modal-content-radius-desktop: var(--radius-md, 0.5rem);
			--mdc-modal-content-max-width-desktop: 27.5rem;
			--mdc-modal-content-max-height-desktop: 90vh;
			--mdc-modal-content-max-height-mobile: 100vh;
			--mdc-modal-content-shadow-desktop: var(--shadow-floating-md, 0px 0px 16px 0px rgba(0, 0, 0, 0.08));
			--mdc-modal-body-padding-mobile: var(--spacing-050, 0.5rem);
			--mdc-modal-body-padding-desktop: var(--spacing-150, 1.5rem);
			--mdc-modal-cta-padding-mobile: var(--spacing-125, 1.25rem);
			--mdc-modal-cta-padding-desktop: var(--spacing-150, 1.5rem);
			--mdc-modal-cta-border-color: var(--color-border-primary, var(--color-functional-gray-75));
			--mdc-modal-cta-shadow-mobile: var(--shadow-floating-sm, 0px 0px 8px 0px rgba(0, 0, 0, 0.16));
			--mdc-modal-cta-margin-top-mobile: var(--spacing-125, 1.25rem);
			font-family: var(--typography-font-family-sans);
		}

		dialog.mdc-modal {
			border: none;
			padding: 0;
			background: transparent;
			max-width: none;
			max-height: none;
			width: 100%;
			height: 100%;
			position: fixed;
			inset: 0;
			overflow: visible;
			pointer-events: none;
		}

		dialog.mdc-modal::backdrop {
			background: transparent;
		}

		.mdc-modal__overlay {
			position: fixed;
			inset: 0;
			background: var(--mdc-modal-overlay-background);
			opacity: 0;
			transition: var(--mdc-modal-overlay-transition);
		}

		.mdc-modal__content {
			position: fixed;
			z-index: 1;
			overflow: hidden;
			width: 100%;
			background-color: var(--mdc-modal-content-background);
			display: flex;
			flex-direction: column;
		}

		.mdc-modal__header {
			display: flex;
			align-items: center;
			gap: var(--spacing-100, 1rem);
			min-height: 2.5rem;
			padding: var(--spacing-075, 0.75rem) var(--spacing-050, 0.5rem);
		}

		.mdc-modal__header ::slotted([slot="header"]) {
			flex: 1;
		}

		.mdc-modal__body {
			overflow-y: auto;
		}

		.mdc-modal__cta .mdc-button-group {
			width: 100%;
		}

		.mdc-modal--no-cta .mdc-modal__cta {
			display: none;
		}

		dialog.mdc-modal.mdc-modal--open {
			pointer-events: auto;
		}

		dialog.mdc-modal.mdc-modal--open .mdc-modal__overlay {
			opacity: 1;
		}

		dialog.mdc-modal.mdc-modal--closing {
			pointer-events: none;
		}

		/* breakpoints.md - 1 */
		@media (max-width: 767px) {
			.mdc-modal__content {
				width: auto;
				bottom: var(--spacing-050);
				left: var(--spacing-050);
				max-height: calc(var(--mdc-modal-content-max-height-mobile) - var(--spacing-100));
				right: var(--spacing-050);
				border-radius: var(--mdc-modal-content-radius-desktop);
				transform: translateY(100%);
				transition: var(--mdc-modal-content-transition);
			}

			.mdc-modal__body {
				padding-left: var(--mdc-modal-body-padding-mobile);
				padding-right: var(--mdc-modal-body-padding-mobile);
				overflow-y: auto;
			}

			.mdc-modal--no-cta .mdc-modal__body {
				padding-bottom: var(--mdc-modal-cta-margin-top-mobile);
			}

			.mdc-modal__cta {
				display: flex;
				justify-content: flex-end;
				padding: var(--mdc-modal-cta-padding-mobile);
				box-shadow: var(--mdc-modal-cta-shadow-mobile);
				margin-top: var(--mdc-modal-cta-margin-top-mobile);
			}

			dialog.mdc-modal.mdc-modal--open .mdc-modal__content {
				transform: translateY(0);
			}
		}

		/* breakpoints.md */
		@media (min-width: 768px) {
			.mdc-modal__content {
				max-width: var(--mdc-modal-content-max-width-desktop);
				width: 100%;
				max-height: var(--mdc-modal-content-max-height-desktop);
				border-radius: var(--mdc-modal-content-radius-desktop);
				left: 50%;
				top: 50%;
				transform: translate(-50%, -50%) scale(0.95);
				opacity: 0;
				transition: var(--mdc-modal-content-transition-desktop);
				margin: auto;
				box-shadow: var(--mdc-modal-content-shadow-desktop);
			}

			dialog.mdc-modal.mdc-modal--open .mdc-modal__content {
				transform: translate(-50%, -50%) scale(1);
				opacity: 1;
			}

			.mdc-modal__header {
				padding: var(--spacing-100, 1rem);
			}

			.mdc-modal__body {
				padding: var(--mdc-modal-body-padding-desktop);
			}

			.mdc-modal__cta {
				display: flex;
				justify-content: flex-end;
				padding: var(--mdc-modal-cta-padding-desktop);
				border-top: 1px solid var(--mdc-modal-cta-border-color);
			}
		}
	`

  render() {
    return html`
			<dialog
				class="mdc-modal${!this.hasCtaSlot ? ' mdc-modal--no-cta' : ''}"
				@cancel=${this.handleCancel}
			>
				<div class="mdc-modal__overlay" @click=${this.handleClose}></div>
				<div class="mdc-modal__content">
					<div class="mdc-modal__header">
						<slot name="header"></slot>
						<mdc-icon-button size="lg" iconSize="default" type="tertiary" icon="close"
							@click=${this.handleClose}></mdc-icon-button>
					</div>
					<div class="mdc-modal__body">
						<slot>${this.content}</slot>
					</div>
					<div class="mdc-modal__cta">
						<slot name="cta" @slotchange=${this.handleCtaSlotChange}></slot>
					</div>
				</div>
			</dialog>
		`
  }
}
